/**
 * Validation handlers extracted from `useForm/index.tsx`.
 *
 * Owns single-field validation, blur-triggered validation + side-effect
 * data sync, full-step validation (including the firstName/lastName
 * full-name special case), tracking which fields had their validation
 * skipped server-side, and applying server-side validation errors to
 * the form state.
 *
 * All handlers preserve the original behavior — they wrap the same
 * helpers (`fieldValidation`, `fullNameValidation`,
 * `canShowFieldAfterDependency`, etc.) the parent hook used inline.
 */

import { Field } from "@lib/shared/types";
import { SectionRules } from "@page-builder/ts/types";
import {
    canShowFieldAfterDependency,
    fieldValidation,
    fullNameValidation,
} from "@lib/shared/form";
import { flattenDeep, mapValues, pick } from "@lib/shared/lodash";
import { getErrorMessageTranslation } from "@lib/sharedUtils";
import {
    ActionTypes,
    FieldsData,
    FormReducerAction,
    InitState,
} from "../types";
import type { SubmitLeadFormResponse } from "src/api/submitLead";
import type { DomainForm, DomainInfo } from "src/api";
import type { FormRuntime } from "../state/runtimeState";

// `Step` is not exported from a canonical place; we only need a structural shape.
interface StepLike {
    fields: Field[];
}

interface UseValidationParams {
    form: DomainForm;
    domain: DomainInfo;
    locale: string;
    selectedCountryCode: string | null | undefined;
    domainNameTranslation: string;
    state: InitState;
    steps: StepLike[];
    dispatch: (action: FormReducerAction) => void;
    runtimeRef: { current: FormRuntime };
    otp: InitState["otp"];
    isPiiCompletionForm: boolean | undefined;
    piiCompletionData?: { missingFields: string[] } | null;
    loadingForm: boolean;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    handleUpdateOffers: (...args: any[]) => any;
    getStepField: (codeName: string) => Field | undefined;
    nextState: () => Promise<InitState>;
    saveSharedFields: (fieldsState: FieldsData) => void;
    sendDataToServer: (args: {
        submit: boolean;
        skippedValidationFields?: string[];
    }) => Promise<SubmitLeadFormResponse>;
    setHasRegulations: (value: boolean) => void;
    setShowPiiCompletionOtherFields: (value: boolean) => void;
    domainEnableRejectedAnswer: boolean;
    rejectedAnswersRules?: SectionRules[][];
    phoneNumber: string | null;
    referrer?: string | null;
    visitDetails?: unknown;
    userAgent?: string | null;
}

export const useValidation = ({
    form,
    domain,
    locale,
    selectedCountryCode,
    domainNameTranslation,
    state,
    steps,
    dispatch,
    runtimeRef,
    otp,
    isPiiCompletionForm,
    piiCompletionData,
    loadingForm,
    handleUpdateOffers,
    getStepField,
    nextState,
    saveSharedFields,
    sendDataToServer,
    setHasRegulations,
    setShowPiiCompletionOtherFields,
    domainEnableRejectedAnswer,
    rejectedAnswersRules,
    phoneNumber,
    referrer,
    visitDetails,
    userAgent,
}: UseValidationParams) => {
    const { fieldsState, formErrors } = state;

    const addFieldToSkippedValidation = (codeName: string) => {
        const skipped = runtimeRef.current.skippedValidationFields;
        if (!skipped.includes(codeName)) {
            skipped.push(codeName);
        }
    };

    const serverSideValidation = ({
        data,
        success,
    }: SubmitLeadFormResponse) => {
        dispatch({ type: ActionTypes.SET_LOADING_FORM, payload: false });
        if (success) {
            if (data?.otpRequired) {
                dispatch({
                    type: ActionTypes.SET_OTP,
                    payload: {
                        ...otp,
                        isRequired: true,
                        countDown: data.otpTime ?? 60,
                    },
                });
                dispatch({
                    type: ActionTypes.SET_FORM_WAITING_OTP,
                    payload: true,
                });
            } else {
                if (data?.operationId) {
                    dispatch({
                        type: ActionTypes.SET_OPERATION,
                        payload: {
                            operationId: data.operationId,
                            key: "ROUND_ONE",
                        },
                    });
                }
            }
        } else if (data?.results) {
            const newFieldsState = { ...fieldsState };

            data?.results?.map((result) => {
                const field = getStepField(result.codeName);

                newFieldsState[result.codeName] = {
                    ...fieldsState[result.codeName],
                    valid: false,
                    errorMessage: getErrorMessageTranslation(
                        domain,
                        result?.errorCode,
                        locale,
                        result.reason ?? field?.errorMessage,
                    ),
                };
            });

            dispatch({
                type: ActionTypes.SET_FORM_ERROR_FIELD,
                payload: { fields: newFieldsState, formErrors: formErrors + 1 },
            });
        } else {
            dispatch({
                type: ActionTypes.SET_FORM_ERROR,
                payload:
                    form.errorMessage ??
                    domain?.translations?.[
                        `${domainNameTranslation}_generalFormErrorHasOccurred`
                    ] ??
                    "An error has occurred, please try again",
            });
        }
    };

    const handleFieldValidation = async (
        field: Field,
        checkRegulations: boolean = true,
        overrideFieldsState?: FieldsData,
    ) => {
        const _fieldsState = overrideFieldsState ?? fieldsState;

        const validCountryCode =
            form?.supportedCountries.length &&
            form?.supportedCountries.find(
                (item) => item.value === selectedCountryCode,
            )
                ? (selectedCountryCode as string)
                : "US";

        const rawValue = _fieldsState[field.codeName]?.value;
        const {
            valid,
            message,
            newValue,
            hasRegulations,
            validationSkipped,
            isRejectedAnswerResult = false,
        } = await fieldValidation(
            field,
            typeof rawValue === "string" ? rawValue.trim() : rawValue,
            locale,
            validCountryCode,
            handleUpdateOffers,
            domain,
            domainEnableRejectedAnswer,
            rejectedAnswersRules,
            {
                phoneNumber,
                referrer,
                visitDetails,
                userAgent,
            },
        );

        if (validationSkipped) {
            addFieldToSkippedValidation(field.codeName);
        }

        if (hasRegulations && checkRegulations) {
            setHasRegulations(true);
            return;
        }

        const newObj: FieldsData = {};
        newObj[field.codeName] = {
            ..._fieldsState[field.codeName],
            valid,
            errorMessage: !valid
                ? field.errorMessage
                    ? field.errorMessage
                    : message
                : "",
            value: (newValue ?? _fieldsState[field.codeName]!.value) as string,
            isRejectedAnswerResult,
        };

        dispatch({
            type: ActionTypes.SET_FIELDS_STATE,
            payload: { ...newObj },
        });
        const { fieldsState: newFieldsState } = await nextState();
        saveSharedFields(newFieldsState);

        return valid;
    };

    const handleFieldBlur = (field: Field) => {
        if (!fieldsState[field.codeName].changed) return;
        if (runtimeRef.current.submitStepClicked) return;
        void handleFieldValidation(field, false);

        if (runtimeRef.current.startSendingData && !loadingForm) {
            if (!runtimeRef.current.isApiCallInProgress) {
                void sendDataToServer({
                    submit: false,
                    skippedValidationFields:
                        runtimeRef.current.skippedValidationFields,
                });
            } else {
                runtimeRef.current.queues.push(() =>
                    sendDataToServer({
                        submit: false,
                        skippedValidationFields:
                            runtimeRef.current.skippedValidationFields,
                    }).then(() => undefined),
                );
            }
        }
    };

    const handleStepValidation = async (
        overrideFieldsState?: FieldsData,
    ): Promise<boolean> => {
        const _fieldsState = overrideFieldsState ?? fieldsState;
        const { currentStep } = state;

        let stepValid = true;
        let fields: Field[] = [];

        if (formErrors) {
            fields = flattenDeep(steps.map((step) => step.fields)).filter(
                (field) => !_fieldsState[(field as Field).codeName].valid,
            ) as Field[];
        } else {
            fields = steps[currentStep].fields;
        }

        dispatch({ type: ActionTypes.SET_LOADING_FORM, payload: true });

        for (const field of fields) {
            const fieldValid = !canShowFieldAfterDependency(field, _fieldsState)
                ? true
                : await handleFieldValidation(field, true, _fieldsState);

            if (!fieldValid) {
                if (
                    isPiiCompletionForm &&
                    stepValid &&
                    !piiCompletionData?.missingFields.includes(field.codeName)
                ) {
                    setShowPiiCompletionOtherFields(true);
                    const { codeName } = field;
                    setTimeout(() => {
                        const element = document.getElementById(codeName);
                        if (element) {
                            element.scrollIntoView({
                                behavior: "smooth",
                                block: "center",
                            });
                        }
                    }, 100);
                }

                stepValid = false;
            }
        }

        if (
            stepValid &&
            steps[currentStep].fields.every((f) =>
                ["firstName", "lastName"].includes(f.codeName),
            )
        ) {
            const { valid: fullNameValid, message: fullNameMessage } =
                await fullNameValidation({
                    ...(pick(mapValues(_fieldsState, "value"), [
                        "firstName",
                        "lastName",
                    ]) as { firstName: string; lastName: string }),
                    domain,
                    locale,
                });

            const newObj: FieldsData = {};

            dispatch({ type: ActionTypes.SET_LOADING_FORM, payload: false });

            const { fieldsState: newFieldsState } = await nextState();

            newObj["firstName"] = {
                ...newFieldsState["firstName"],
                valid: fullNameValid,
                errorMessage: fullNameMessage,
            };
            newObj["lastName"] = {
                ...newFieldsState["lastName"],
                valid: fullNameValid,
                errorMessage: fullNameMessage,
            };

            dispatch({
                type: ActionTypes.SET_FIELDS_STATE,
                payload: { ...newObj },
            });

            if (!fullNameValid) {
                stepValid = false;
            }
        }

        dispatch({
            type: ActionTypes.SET_LOADING_FORM,
            payload: false,
        });
        dispatch({ type: ActionTypes.SET_STEP_STATUS, payload: stepValid });
        return stepValid;
    };

    return {
        addFieldToSkippedValidation,
        serverSideValidation,
        handleFieldValidation,
        handleFieldBlur,
        handleStepValidation,
    };
};
